import React, { useMemo } from 'react';
import type { ContactRow } from '@shared/flow/contact-view';
import { useContactIntelSummary } from './hooks';
import { renderBoldText } from '../renderBoldText';
import { toContactIntelFields } from './contactIntelFields';

/**
 * Contact Intelligence panel (D6, plan
 * `plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`
 * §3b item 3) — the 3rd panel in `ContactDetailPanels.tsx`'s D7 order.
 *
 * `contact` is derived as a PLAIN OBJECT LITERAL off the already-validated
 * `row` prop via `toContactIntelFields` (plan 005 Wave 0 U1a — lifted into
 * `contactIntelFields.ts` so the V2 band's Contact Intelligence card builds
 * the identical wire projection) — no `ContactIntelContactFieldsSchema.parse`
 * at render (the row already crossed the trust boundary once, at
 * `GET /api/flow/contacts`).
 */
export function ContactIntelPanel({ row }: { row: ContactRow }) {
  const contact = useMemo(() => toContactIntelFields(row), [row]);
  const { text, isStreaming, error, regenerate } = useContactIntelSummary(contact, true);

  if (error) {
    return (
      <div>
        <p className="text-sm text-content-danger dark:text-red-400">Intelligence unavailable.</p>
        <button
          type="button"
          onClick={regenerate}
          className="mt-1.5 text-[11px] font-medium text-content-action dark:text-blueberry-400 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="whitespace-pre-wrap text-sm text-content-primary dark:text-nightshade-300">
        {renderBoldText(text)}
        {isStreaming && <span className="animate-pulse" aria-hidden="true">▍</span>}
      </div>
      {!isStreaming && text && (
        <button
          type="button"
          onClick={regenerate}
          className="mt-2 text-[11px] font-medium text-content-action dark:text-blueberry-400 hover:underline"
        >
          Regenerate
        </button>
      )}
    </div>
  );
}
